/**
 * SeasonalThemesTab — admin UI para temas estacionales en /platform.
 *
 * Lista + form de create/edit/delete. Los cambios afectan a todos los
 * tenants que matcheen country + fecha.
 */
import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Eye, EyeOff, Calendar, Save, X } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import {
  useSeasonalThemes,
  useCreateSeasonalTheme,
  useUpdateSeasonalTheme,
  useDeleteSeasonalTheme,
  type SeasonalThemeDto,
} from '../hooks/usePlatform'
import { ToastContainer, useToast } from '@/components/Toast'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { extractErrorMessage } from '@/lib/errorUtils'
import { Button } from '@/components/Button'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { inputCls, labelCls } from '@/lib/formStyles'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

const COUNTRY_OPTIONS = [
  { value: '', label: 'Todos los países (LATAM)' },
  { value: 'CL', label: 'Chile' },
  { value: 'PE', label: 'Perú' },
  { value: 'AR', label: 'Argentina' },
  { value: 'MX', label: 'México' },
  { value: 'EC', label: 'Ecuador' },
]

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']

type ThemeDraft = Omit<Partial<SeasonalThemeDto>, 'id' | 'created_at' | 'updated_at'>

const EMPTY_DRAFT: ThemeDraft = {
  name: '',
  country: '',
  start_month: 1,
  start_day: 1,
  end_month: 1,
  end_day: 7,
  priority: 5,
  emoji: '🎉',
  message: '',
  gradient_light: 'linear-gradient(90deg, #7C3AED 0%, #EC4899 50%, #F59E0B 100%)',
  gradient_dark:  'linear-gradient(90deg, #4c1d95 0%, #831843 50%, #78350f 100%)',
  text_color: '#ffffff',
  is_active: true,
}

export function SeasonalThemesTab() {
  const { data, isLoading } = useSeasonalThemes()
  const themes = data?.themes ?? []
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const [editing, setEditing] = useState<SeasonalThemeDto | null>(null)
  const [creating, setCreating] = useState(false)

  const { mutate: deleteTheme } = useDeleteSeasonalTheme()

  const handleDelete = (theme: SeasonalThemeDto) => {
    confirm({
      message: `¿Eliminar el tema "${theme.name}"? Los tenants dejarán de verlo.`,
      variant: 'danger',
      onConfirm: () => {
        deleteTheme(theme.id, {
          onSuccess: () => toast.success('Tema eliminado.'),
          onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al eliminar')),
        })
      },
    })
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-content-muted">
          Banners decorativos que se muestran automáticamente según país + fecha.
        </p>
        <Button leftIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
          Nuevo tema
        </Button>
      </div>

      <DataTable
        columns={[
          {
            key: 'name', header: 'Nombre',
            render: t => <span className="font-medium text-content"><span className="mr-1.5">{t.emoji}</span>{t.name}</span>,
          },
          { key: 'country', header: 'País', render: t => t.country || 'LATAM' },
          {
            key: 'rango', header: 'Rango',
            render: t => (
              <span className="text-xs font-mono">
                {String(t.start_day).padStart(2, '0')}/{MONTHS[t.start_month - 1]} – {String(t.end_day).padStart(2, '0')}/{MONTHS[t.end_month - 1]}
              </span>
            ),
          },
          {
            key: 'message', header: 'Mensaje',
            render: t => <span className="text-xs block max-w-sm truncate">{t.message}</span>,
          },
          {
            key: 'is_active', header: 'Estado',
            render: t => t.is_active
              ? <span className="inline-flex items-center gap-1 text-xs text-success-content-soft"><Eye size={12} />Activo</span>
              : <span className="inline-flex items-center gap-1 text-xs text-content-subtle"><EyeOff size={12} />Inactivo</span>,
          },
          {
            key: 'acciones', header: 'Acciones', width: 112,
            render: t => (
              <div className="flex items-center justify-center gap-1">
                <button onClick={() => setEditing(t)} title="Editar"
                  className="p-1.5 rounded hover:bg-soft text-content-muted transition-colors">
                  <Pencil size={13} />
                </button>
                <button onClick={() => handleDelete(t)} title="Eliminar"
                  className="p-1.5 rounded hover:bg-danger-surface text-content-muted hover:text-danger-content-soft transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            ),
          },
        ]}
        rows={themes}
        rowKey={t => t.id}
        loading={isLoading}
        empty={<EmptyState icon={EyeOff} size="sm" title="Sin temas configurados." />}
        minWidth={860}
        caption="Temas estacionales de la plataforma"
      />

      {creating && (
        <ThemeFormModal
          onClose={() => setCreating(false)}
          onSuccessMessage="Tema creado."
        />
      )}
      {editing && (
        <ThemeFormModal
          theme={editing}
          onClose={() => setEditing(null)}
          onSuccessMessage="Tema actualizado."
        />
      )}

      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Form modal (crear / editar) ─────────────────────────────────────────────

function ThemeFormModal({
  theme, onClose, onSuccessMessage,
}: {
  theme?: SeasonalThemeDto
  onClose: () => void
  onSuccessMessage: string
}) {
  const toast = useToast()
  const [draft, setDraft] = useState<ThemeDraft>(theme ?? EMPTY_DRAFT)
  const { mutate: create, isPending: isCreating } = useCreateSeasonalTheme()
  const { mutate: update, isPending: isUpdating } = useUpdateSeasonalTheme(theme?.id ?? '')
  const isPending = isCreating || isUpdating

  useEffect(() => {
    if (theme) setDraft(theme)
  }, [theme])

  const set = <K extends keyof ThemeDraft>(k: K, v: ThemeDraft[K]) => setDraft(d => ({ ...d, [k]: v }))

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const action = theme ? update : create
    action(draft as any, {
      onSuccess: () => { toast.success(onSuccessMessage); onClose() },
      onError: (err: any) => toast.error(extractErrorMessage(err, 'Error al guardar')),
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={theme ? `Editar: ${theme.name}` : 'Nuevo tema estacional'}
      actions={
        <>
          <ModalCancelButton onClick={onClose} />
          <button
            type="submit"
            form="seasonal-theme-form"
            disabled={isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-on-primary bg-primary hover:bg-primary-hover rounded-lg disabled:opacity-60 transition-colors"
          >
            <Save size={14} />
            {isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <form id="seasonal-theme-form" onSubmit={onSubmit} className="space-y-3">
        {/* Preview */}
        <div
          className="rounded-lg flex items-center justify-center gap-3 px-6 py-3 text-sm font-semibold"
          style={{
            background: draft.gradient_light,
            color: draft.text_color,
            boxShadow: 'inset 0 -2px 4px 0 rgb(0 0 0 / 0.15)',
          }}
        >
          <span className="text-xl">{draft.emoji}</span>
          <span style={{ textShadow: '0 1px 3px rgb(0 0 0 / 0.35)' }}>{draft.message || '(sin mensaje)'}</span>
          <span className="text-xl">{draft.emoji}</span>
        </div>
        <p className="text-xs text-content-subtle text-center -mt-2">Preview (light mode)</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            dense
            label="Nombre *"
            value={draft.name ?? ''}
            onChange={e => set('name', e.target.value)}
            placeholder="Navidad, Halloween, ..."
            inputProps={{ required: true }}
          />
          <SelectField
            dense
            label="País"
            value={draft.country ?? ''}
            onChange={e => set('country', e.target.value)}
          >
            {COUNTRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectField>

          <div className="sm:col-span-2 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="seasonalthemestab-inicio-mes-dia" className={labelCls}>Inicio — Mes/Día *</label>
              <div className="flex gap-2">
                <select id="seasonalthemestab-inicio-mes-dia" className={inputCls} value={draft.start_month ?? 1}
                  onChange={e => set('start_month', parseInt(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <input aria-label="Día de inicio" type="number" min={1} max={31} className={inputCls}
                  value={draft.start_day ?? 1}
                  onChange={e => set('start_day', parseInt(e.target.value) || 1)} />
              </div>
            </div>
            <div>
              <label htmlFor="seasonalthemestab-fin-mes-dia" className={labelCls}>Fin — Mes/Día *</label>
              <div className="flex gap-2">
                <select id="seasonalthemestab-fin-mes-dia" className={inputCls} value={draft.end_month ?? 1}
                  onChange={e => set('end_month', parseInt(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <input aria-label="Día de término" type="number" min={1} max={31} className={inputCls}
                  value={draft.end_day ?? 1}
                  onChange={e => set('end_day', parseInt(e.target.value) || 1)} />
              </div>
            </div>
          </div>

          <TextField
            dense
            label="Emoji *"
            value={draft.emoji ?? ''}
            onChange={e => set('emoji', e.target.value)}
            placeholder="🎄"
            inputProps={{ required: true, maxLength: 10 }}
          />
          <TextField
            dense
            label="Prioridad"
            value={draft.priority ?? 5}
            onChange={e => set('priority', parseInt(e.target.value) || 1)}
            inputProps={{ type: 'number', min: 1, max: 99 }}
          />

          <TextField
            dense
            className="sm:col-span-2"
            label="Mensaje *"
            value={draft.message ?? ''}
            onChange={e => set('message', e.target.value)}
            placeholder="¡Feliz Navidad! Que tengas un próspero cierre de año"
            inputProps={{ required: true, maxLength: 200 }}
          />

          <TextField
            dense
            label="Gradient Light"
            value={draft.gradient_light ?? ''}
            onChange={e => set('gradient_light', e.target.value)}
          />
          <TextField
            dense
            label="Gradient Dark"
            value={draft.gradient_dark ?? ''}
            onChange={e => set('gradient_dark', e.target.value)}
          />

          <div>
            <label htmlFor="seasonalthemestab-color-de-texto" className={labelCls}>Color de texto</label>
            <div className="flex gap-2 items-center">
              <input id="seasonalthemestab-color-de-texto"
                type="color"
                value={draft.text_color ?? '#ffffff'}
                onChange={e => set('text_color', e.target.value)}
                className="h-10 w-16 rounded-lg border border-divider cursor-pointer p-1 bg-surface"
              />
              <input aria-label="Color del texto (hexadecimal)" className={`${inputCls} font-mono text-xs`} value={draft.text_color ?? ''}
                onChange={e => set('text_color', e.target.value)} />
            </div>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.is_active ?? true}
                onChange={e => set('is_active', e.target.checked)}
                className="w-4 h-4 rounded border-divider-strong text-primary focus:ring-primary"
              />
              <span className="text-sm text-content">Tema activo (visible para tenants)</span>
            </label>
          </div>
        </div>
      </form>
    </Modal>
  )
}
